import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT } from "@/data/content";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const ENQUIRY_TYPES = ["General", "Dealer / Distributor", "Bulk Order", "Export", "Quotation"];

const emptyForm = { name: "", email: "", phone: "", company: "", enquiry_type: "General", message: "" };

const Contact = () => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { t, lang } = useLanguage();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error(lang === "te" ? "దయచేసి మీ పేరు, ఇమెయిల్, ఫోన్ మరియు సందేశాన్ని నింపండి." : "Please fill in your name, email, phone and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success(lang === "te" ? "మీ విచారణ పంపబడింది! మా బృందం త్వరలోనే మిమ్మల్ని సంప్రదిస్తుంది." : "Enquiry sent! Our team will get back to you shortly.");
      setForm(emptyForm);
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (lang === "te" ? "చిన్న సమస్య ఏర్పడింది. దయచేసి వాట్సాప్ లేదా ఫోన్ ద్వారా సంప్రదించండి." : "Something went wrong. Please try WhatsApp or call us."));
    } finally {
      setSubmitting(false);
    }
  };

  const infoCards = [
    { icon: MapPin, label: t("contact.visitUs"), value: CONTACT.addressLines.join(", "), href: null, testid: "contact-address" },
    { icon: Phone, label: t("contact.callUs"), value: CONTACT.phoneDisplay, href: `tel:${CONTACT.phoneDial}`, testid: "contact-phone" },
    { icon: Mail, label: t("contact.emailUs"), value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
  ];

  return (
    <section id="contact" className="relative py-24 md:py-36 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          {/* Left — info */}
          <div>
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-[#d4af37]" />
              <span className="eyebrow">{t("contact.eyebrow")}</span>
            </div>
            <Reveal>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#222222] mt-6 leading-[1.03]">
                {t("contact.title")}<br />
                <span className="text-[#40916c]">{t("contact.titleHighlight")}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="font-body text-base md:text-lg text-[#5b6b62] leading-relaxed mt-6 max-w-md">
                {t("contact.subtitle")}
              </p>
            </Reveal>

            <div className="mt-10 space-y-4">
              {infoCards.map((c) => {
                const Icon = c.icon;
                const inner = (
                  <div className="flex items-center gap-5 rounded-2xl border border-[#95d5b2]/40 bg-[#f8fff8] p-5 hover:border-[#40916c]/40 hover:shadow-lg hover:shadow-[#2d6a4f]/8 transition-all">
                    <div className="h-12 w-12 rounded-xl bg-[#d8f3dc] flex items-center justify-center text-[#2d6a4f] shrink-0">
                      <Icon size={22} strokeWidth={1.8} />
                    </div>
                    <div>
                      <p className="eyebrow text-[10px]">{c.label}</p>
                      <p className="font-body text-[15px] text-[#222222] mt-1">{c.value}</p>
                    </div>
                  </div>
                );
                return c.href ? (
                  <a key={c.label} href={c.href} data-testid={c.testid}>{inner}</a>
                ) : (
                  <div key={c.label} data-testid={c.testid}>{inner}</div>
                );
              })}

              <a
                href={`https://wa.me/${CONTACT.whatsapp}?text=Hello%20Gupta%20Enterprises%2C%20I%27d%20like%20to%20enquire%20about%20Vasavi%20Gold%20rice.`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2.5 rounded-2xl bg-[#25D366] text-white font-body font-medium py-4 hover:bg-[#1fb356] transition-colors"
                data-testid="contact-whatsapp-btn"
              >
                <MessageCircle size={19} /> {t("contact.chatWhatsapp")}
              </a>
            </div>

            {/* Map placeholder */}
            <Reveal delay={0.1}>
              <div className="mt-6 rounded-2xl overflow-hidden border border-[#95d5b2]/40 h-56" data-testid="contact-map">
                <iframe
                  title="Gupta Enterprises location"
                  className="w-full h-full grayscale-[0.15]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapQuery)}&output=embed`}
                />
              </div>
            </Reveal>
          </div>

          {/* Right — form */}
          <Reveal delay={0.12}>
            <motion.form
              onSubmit={submit}
              className="relative rounded-[30px] bg-[#f8fff8] border border-[#95d5b2]/40 p-8 md:p-10 shadow-xl shadow-[#2d6a4f]/5"
              data-testid="enquiry-form"
            >
              <h3 className="font-display text-2xl font-semibold text-[#222222]">{t("contact.formTitle")}</h3>
              <p className="font-body text-sm text-[#5b6b62] mt-2">{t("contact.formSubtitle")}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8">
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-body text-[13px] text-[#3a4a41]">{t("contact.nameLabel")}</Label>
                  <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("contact.namePlaceholder")} className="rounded-xl border-[#95d5b2]/50 bg-white h-11 focus-visible:ring-[#40916c]" data-testid="form-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="font-body text-[13px] text-[#3a4a41]">{t("contact.phoneLabel")}</Label>
                  <Input id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("contact.phonePlaceholder")} className="rounded-xl border-[#95d5b2]/50 bg-white h-11 focus-visible:ring-[#40916c]" data-testid="form-phone" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-body text-[13px] text-[#3a4a41]">{t("contact.emailLabel")}</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder={t("contact.emailPlaceholder")} className="rounded-xl border-[#95d5b2]/50 bg-white h-11 focus-visible:ring-[#40916c]" data-testid="form-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company" className="font-body text-[13px] text-[#3a4a41]">{t("contact.companyLabel")}</Label>
                  <Input id="company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder={t("contact.companyPlaceholder")} className="rounded-xl border-[#95d5b2]/50 bg-white h-11 focus-visible:ring-[#40916c]" data-testid="form-company" />
                </div>
              </div>

              <div className="space-y-2 mt-5">
                <Label className="font-body text-[13px] text-[#3a4a41]">{t("contact.typeLabel")}</Label>
                <Select value={form.enquiry_type} onValueChange={(v) => set("enquiry_type", v)}>
                  <SelectTrigger className="rounded-xl border-[#95d5b2]/50 bg-white h-11 focus:ring-[#40916c]" data-testid="form-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENQUIRY_TYPES.map((tItem) => (
                      <SelectItem key={tItem} value={tItem} data-testid={`form-type-option-${tItem}`}>{tItem}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 mt-5">
                <Label htmlFor="message" className="font-body text-[13px] text-[#3a4a41]">{t("contact.msgLabel")}</Label>
                <Textarea id="message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder={t("contact.msgPlaceholder")} rows={4} className="rounded-xl border-[#95d5b2]/50 bg-white focus-visible:ring-[#40916c] resize-none" data-testid="form-message" />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="mt-8 w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#40916c] text-white font-body font-medium py-4 hover:bg-[#2d6a4f] transition-all hover:shadow-lg hover:shadow-[#40916c]/25 disabled:opacity-70"
                data-testid="form-submit"
              >
                {submitting ? (
                  <><Loader2 size={18} className="animate-spin" /> {t("contact.submittingBtn")}</>
                ) : done ? (
                  <><CheckCircle2 size={18} /> {t("contact.submittedBtn")}</>
                ) : (
                  <><Send size={18} /> {t("contact.submitBtn")}</>
                )}
              </button>
            </motion.form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
